import { Flag } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useMe, useOverview, useSummaries } from "@/api/hooks";
import type { Overview, WindowSlot } from "@/api/types";
import { PageHeader } from "@/components/PageHeader";
import { StatusChip } from "@/components/StatusChip";
import { Banner } from "@/components/ui/Banner";
import { Spinner } from "@/components/ui/Spinner";
import { useI18n } from "@/i18n";
import { cx } from "@/lib/cx";

function elapsedFraction(slot: WindowSlot, now: number) {
  const start = new Date(slot.start).getTime();
  const end = new Date(slot.end).getTime();
  return Math.min(1, Math.max(0, (now - start) / (end - start)));
}

function SlotState({ slot }: { slot: WindowSlot }) {
  const { t } = useI18n();
  if (slot.summary) return <StatusChip status={slot.summary.status} />;
  if (slot.run) return <StatusChip status={slot.run.status} />;
  if (slot.state === "current")
    return <span className="text-xs text-accent-text">{t("overview.inProgress")}</span>;
  if (slot.state === "upcoming")
    return <span className="text-xs text-muted">{t("overview.upcoming")}</span>;
  return <span className="text-xs text-muted">{t("overview.noRun")}</span>;
}

function WindowCourse({ slot, now }: { slot: WindowSlot; now: number }) {
  const { t, fmt } = useI18n();
  const navigate = useNavigate();
  const summary = slot.summary;
  const failed = slot.run?.status === "failed";
  const clickable = Boolean(summary);
  const content = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span
          className={cx(
            "num text-sm",
            slot.state === "current" ? "font-semibold text-text" : "text-muted",
          )}
        >
          {fmt.time(slot.start)}–{fmt.time(slot.end)}
        </span>
        <SlotState slot={slot} />
      </div>
      {summary ? (
        <p
          lang="ar"
          dir="rtl"
          className="news line-clamp-3 text-start text-lg leading-snug font-semibold"
        >
          {summary.flagged && (
            <Flag className="me-1 inline size-3.5 text-warning" aria-label={t("review.flagged")} />
          )}
          {summary.headline}
        </p>
      ) : failed ? (
        <p className="line-clamp-3 text-sm text-danger">
          {slot.run?.error || t("overview.runFailed")}
        </p>
      ) : (
        <p className="text-sm text-muted">
          {slot.state === "upcoming"
            ? ""
            : slot.run?.status === "empty"
              ? t("overview.quietWindow")
              : ""}
        </p>
      )}
      <div className="mt-auto text-xs text-muted">
        {slot.run && slot.run.message_count > 0 && (
          <span className="num">
            {t("overview.messagesFolded", {
              n: fmt.number(slot.run.message_count),
              d: fmt.number(slot.run.duplicate_count),
            })}
          </span>
        )}
      </div>
      {slot.state === "current" && (
        <span
          className="absolute inset-x-0 bottom-0 h-[3px] origin-left bg-accent rtl:origin-right"
          style={{
            transform: `scaleX(${elapsedFraction(slot, now)})`,
            animation: "window-fill 900ms cubic-bezier(.2,.7,.2,1)",
          }}
          aria-hidden
        />
      )}
    </>
  );
  const inner = cx(
    "flex h-full w-full flex-col gap-2.5 p-4 text-start",
    slot.state === "upcoming" ? "min-h-24" : "min-h-40",
    clickable &&
      "cursor-pointer transition-colors hover:bg-[color-mix(in_oklab,var(--c-primary)_6%,transparent)]",
  );
  return (
    <li className={cx("relative", slot.state === "upcoming" && "text-muted")}>
      {clickable ? (
        <button type="button" className={inner} onClick={() => navigate(`/review/${summary!.id}`)}>
          {content}
        </button>
      ) : (
        <div className={inner}>{content}</div>
      )}
    </li>
  );
}

function Figure({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: "alert";
}) {
  return (
    <div className="flex min-w-36 flex-1 flex-col gap-1 px-5 py-4 first:ps-0">
      <dt className="text-sm text-muted">{label}</dt>
      <dd className={cx("num text-2xl font-semibold", tone === "alert" && "text-danger")}>
        {value}
      </dd>
      {note && <dd className="text-xs text-muted">{note}</dd>}
    </div>
  );
}

function Figures({ data }: { data: Overview }) {
  const { t, fmt } = useI18n();
  return (
    <dl className="flex flex-wrap divide-line border-y border-line sm:divide-x rtl:sm:divide-x-reverse">
      <Figure
        label={t("overview.draftsWaiting")}
        value={fmt.number(data.drafts_awaiting)}
        note={
          data.flagged_awaiting
            ? t("overview.flaggedNote", { n: fmt.number(data.flagged_awaiting) })
            : undefined
        }
      />
      <Figure label={t("overview.approvedWaiting")} value={fmt.number(data.approved_unpublished)} />
      <Figure
        label={t("overview.publishFailures")}
        value={fmt.number(data.publish_failures_24h)}
        tone={data.publish_failures_24h ? "alert" : undefined}
      />
      <Figure
        label={t("overview.messagesToday")}
        value={fmt.number(data.messages_today)}
        note={t("overview.foldedNote", { n: fmt.number(data.duplicates_today) })}
      />
      {data.month_spend_usd !== undefined && (
        <Figure
          label={t("overview.spend")}
          value={fmt.usd(data.month_spend_usd)}
          note={t("overview.ofBudget", { budget: fmt.usd(data.monthly_budget_usd ?? 0, 0) })}
        />
      )}
    </dl>
  );
}

function Listener({ data }: { data: Overview }) {
  const { t, fmt } = useI18n();
  const { data: me } = useMe();
  return (
    <section>
      <h2 className="text-lg font-semibold">{t("overview.listener")}</h2>
      {data.listener.accounts.length === 0 ? (
        <p className="mt-2 text-sm text-muted">
          {t("overview.noAccounts")}{" "}
          {me?.is_admin && (
            <Link to="/telegram" className="font-medium text-accent-text hover:underline">
              {t("overview.addAccount")}
            </Link>
          )}
        </p>
      ) : (
        <ul className="mt-3 flex flex-col gap-2.5">
          {data.listener.accounts.map((account) => (
            <li key={account.id} className="flex items-start gap-3 text-sm">
              <span
                className={cx(
                  "mt-1.5 size-2 shrink-0 rounded-full",
                  account.is_listening ? "bg-success" : "bg-danger",
                )}
                aria-hidden
              />
              <div className="min-w-0">
                <p>
                  <span className="font-medium">{account.label}</span>{" "}
                  <span className="text-muted">
                    {account.is_listening
                      ? t("overview.listening")
                      : account.last_seen
                        ? t("overview.lastSeen", { when: fmt.relative(account.last_seen) })
                        : t("overview.neverConnected")}
                  </span>
                </p>
                {!account.is_listening && account.last_error && (
                  <p className="mt-0.5 text-xs text-danger">{account.last_error}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-sm text-muted">
        {t("overview.channelsActive", {
          n: fmt.number(data.active_channels),
          m: fmt.number(data.messages_last_3h),
        })}
      </p>
    </section>
  );
}

function WaitingForReview() {
  const { t, fmt } = useI18n();
  const { data } = useSummaries({ status: "draft" });
  const items = data?.results.slice(0, 5) ?? [];
  return (
    <section>
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-lg font-semibold">{t("overview.waiting")}</h2>
        {data && data.count > 0 && (
          <Link to="/review" className="text-sm font-medium text-accent-text hover:underline">
            {t("overview.openQueue")}
          </Link>
        )}
      </div>
      {items.length === 0 ? (
        <p className="mt-2 text-sm text-muted">{t("overview.queueEmpty")}</p>
      ) : (
        <ul className="mt-2 divide-y divide-line">
          {items.map((summary) => (
            <li key={summary.id}>
              <Link to={`/review/${summary.id}`} className="group flex items-start gap-4 py-3">
                <span className="num w-24 shrink-0 pt-1 text-xs text-muted">
                  {fmt.time(summary.run.window_start)}–{fmt.time(summary.run.window_end)}
                </span>
                <span
                  lang="ar"
                  dir="rtl"
                  className="news min-w-0 flex-1 text-start text-lg leading-snug font-medium group-hover:text-accent-text"
                >
                  {summary.sensitivity_flag && (
                    <Flag
                      className="me-1 inline size-3.5 text-warning"
                      aria-label={t("review.flagged")}
                    />
                  )}
                  {summary.headline}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function OverviewPage() {
  const { t, fmt } = useI18n();
  const { data, isLoading, error } = useOverview();
  const [now] = useState(() => Date.now());

  if (isLoading) {
    return (
      <div className="grid min-h-[40dvh] place-items-center text-muted">
        <Spinner className="size-5" label={t("common.loading")} />
      </div>
    );
  }
  if (error || !data) {
    return (
      <Banner tone="danger" title={t("common.loadFailed")}>
        {error?.message}
      </Banner>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title={fmt.longDate(data.now)}
        description={
          <>
            {t("overview.writtenBy")} <bdi dir="ltr">{data.model_id}</bdi>
          </>
        }
        actions={
          <span className="text-sm text-muted">
            {data.auto_approve ? t("overview.autoApproveOn") : t("overview.autoApproveOff")}
          </span>
        }
      />

      {data.critical_alerts.length > 0 && (
        <div className="flex flex-col gap-2">
          {data.critical_alerts.map((alert) => (
            <Banner
              key={alert.id}
              tone="danger"
              title={alert.subject.replace("[Syria3Hours] ", "")}
              action={
                <Link
                  to="/notifications"
                  className="text-sm font-medium text-danger hover:underline"
                >
                  {t("overview.viewAlert")}
                </Link>
              }
            >
              {fmt.relative(alert.created_at)}
            </Banner>
          ))}
        </div>
      )}
      {data.unemailed_critical > 0 && (
        <Banner tone="warning" title={t("overview.emailFailingTitle")}>
          {t("overview.emailFailingBody", { n: fmt.number(data.unemailed_critical) })}
        </Banner>
      )}

      <section aria-labelledby="windows-heading">
        <h2 id="windows-heading" className="mb-3 text-lg font-semibold">
          {t("overview.windows")}
        </h2>
        <ol className="ablaq grid overflow-hidden rounded-[var(--radius-panel)] border border-line sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-8">
          {data.windows.map((slot) => (
            <WindowCourse key={slot.start} slot={slot} now={now} />
          ))}
        </ol>
      </section>

      <Figures data={data} />

      <div className="grid gap-10 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <WaitingForReview />
        <Listener data={data} />
      </div>
    </div>
  );
}
